export type LifetimeState = 'open' | 'overdue' | 'closed';

export interface Lifetime {
    start: number;
    end: number;
    now: number;
    state: LifetimeState;
}

export interface LifetimeBarParts {
    scaleEnd: number;
    elapsed: number;
    overdueFrom: number | null;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function lifetimeOf(
    start: Date | null,
    end: Date | null,
    closed: boolean,
    nowMs: number
): Lifetime | null {
    if (!start || !end) return null;
    const s = start.getTime();
    const e = end.getTime();
    if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;
    const state: LifetimeState = closed
        ? 'closed'
        : nowMs > e
          ? 'overdue'
          : 'open';
    return { start: s, end: e, now: nowMs, state };
}

export function formatSpan(ms: number): string {
    const abs = Math.abs(ms);
    if (abs >= 2 * DAY) return `${Math.floor(abs / DAY)}d`;
    if (abs >= HOUR) return `${Math.floor(abs / HOUR)}h`;
    return `${Math.max(1, Math.floor(abs / MINUTE))}m`;
}

export function lifetimeText(life: Lifetime): string {
    if (life.state === 'closed') return 'Closed';
    if (life.state === 'overdue')
        return `${formatSpan(life.now - life.end)} over`;
    return `${formatSpan(life.end - life.now)} left`;
}

export function lifetimeProgress(life: Lifetime): number {
    if (life.state !== 'open') return 1;
    const share = (life.now - life.start) / (life.end - life.start);
    return Math.min(1, Math.max(0, share));
}

export function lifetimeParts(life: Lifetime): LifetimeBarParts {
    const scaleEnd = life.state === 'overdue' ? life.now : life.end;
    const span = scaleEnd - life.start;
    const at = (t: number) => Math.min(1, Math.max(0, (t - life.start) / span));
    return {
        scaleEnd,
        elapsed: life.state === 'open' ? at(life.now) : 1,
        overdueFrom: life.state === 'overdue' ? at(life.end) : null,
    };
}

export function lifetimeWindow(
    life: Lifetime,
    range: readonly [number, number]
): [number, number] {
    const { scaleEnd } = lifetimeParts(life);
    const span = scaleEnd - life.start;
    const at = (t: number) => Math.min(1, Math.max(0, (t - life.start) / span));
    return [at(range[0]), at(range[1])];
}

export interface QuickRange {
    id: string;
    label: string;
    spanMs: number;
}

export const QUICK_RANGES: readonly QuickRange[] = [
    { id: '1h', label: '1H', spanMs: HOUR },
    { id: '1d', label: '1D', spanMs: DAY },
    { id: '1w', label: '1W', spanMs: 7 * DAY },
    { id: '1m', label: '1M', spanMs: 30 * DAY },
];

const ACTIVE_TOLERANCE_MS = 10 * MINUTE;

export function quickRange(
    spanMs: number,
    bounds: readonly [Date, Date],
    anchorMs: number
): [Date, Date] | null {
    const start = bounds[0].getTime();
    const anchor = Math.max(start, Math.min(anchorMs, bounds[1].getTime()));
    if (anchor - start <= spanMs) return null;
    return [new Date(anchor - spanMs), new Date(anchor)];
}

export function isQuickRange(
    range: readonly [Date, Date] | null,
    spanMs: number,
    anchorMs: number
): boolean {
    if (!range) return false;
    return (
        Math.abs(range[1].getTime() - anchorMs) <= ACTIVE_TOLERANCE_MS &&
        Math.abs(range[1].getTime() - range[0].getTime() - spanMs) <=
            ACTIVE_TOLERANCE_MS
    );
}
